<template>
    <div>
        <v-dialog
                v-model="dialogModel"
                :width="600"
                :fullscreen="$vuetify.breakpoint.xsOnly"
                @click:outside="cancel()"
        >
            <template v-slot:activator="{ on }">
                <v-text-field
                    :value="getLabel"
                    readonly
                    hide-details
                    v-on="on"
                    :clearable="clearable"
                    class="data-range__btn"
                    :style="getStyle"
                    :label="label"
                    :outlined="outlined"
                    :dense="dense"
                    @click:clear="clearDate"
                ></v-text-field>
            </template>
            <v-card
                    class="date-range"
            >
                <v-card-text
                        :class="{
                            'pa-5': !$vuetify.breakpoint.xsOnly,
                            'c-pa-10': $vuetify.breakpoint.xsOnly
                        }"
                >
                    <twin-dates>
                        <template v-slot:left>
                            <date-input
                                    v-model="localDateStartDialog"
                                    data-vv-name="start_at"
                                    :error-messages="errors.collect('start_at')"
                                    label="msg.dateStart"
                                    :width="0"
                                    ref="start_at"
                                    :clearable="clearable"
                            ></date-input>
                        </template>
                        <template v-slot:right>
                            <date-input
                                    v-model="localDateEndDialog"
                                    data-vv-name="end_at"
                                    :error-messages="errors.collect('end_at')"
                                    v-validate="'date_order:start_at'"
                                    label="msg.dateEnd"
                                    :width="0"
                                    :clearable="clearable"
                            ></date-input>
                        </template>
                    </twin-dates>
                    <div
                            class="date-range__presets"
                            :class="{'date-range__presets_mobile': $vuetify.breakpoint.xsOnly}"
                    >
                        <div
                                v-for="p in presets"
                                class="date-range__presets-col"
                        >
                            <div
                                    v-for="(item, key) in p"
                                    :key="key"
                                    class="date-range__preset"
                                    @click="setPreset(item.date_start, item.date_end)"
                            >
                                {{ item.label }}
                            </div>
                        </div>
                    </div>
                    <div class="d-flex justify-end pt-2">
                        <v-btn
                                color="primary"
                                @click="cancel()"
                                class="mr-3"
                                elevation="0"
                        >{{ $t('msg.cancel') }}</v-btn>
                        <v-btn
                                color="primary"
                                @click="apply()"
                                elevation="0"
                        >{{ $t('msg.apply') }}</v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { format, subDays, subMonths } from 'date-fns';
    import TwinDates from './TwinDates';
    import DateInput from './Date';

    export default {
        components: {TwinDates, DateInput},
        props: {
            dateStart: null,
            dateEnd: null,
            width: {type: Number, default: null},
            label: String,
            outlined: {type: Boolean, default: false},
            dense: {type: Boolean, default: false},
            clearable: {type: Boolean, default: false}
        },
        data() {
            return {
                dialogModel: false,
                localDateStart: this.dateStart,
                localDateEnd: this.dateEnd,
                localDateStartDialog: this.dateStart,
                localDateEndDialog: this.dateEnd,
                presets: [
                    [
                        {label: this.$i18n.t('msg.today'),
                            date_start: format(new Date(), 'yyyy-MM-dd'),
                            date_end: format(new Date(), 'yyyy-MM-dd')},
                        {label: this.$i18n.t('msg.yesterday'),
                            date_start: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
                            date_end: format(subDays(new Date(), 1), 'yyyy-MM-dd')},
                        {label: this.$i18n.tc('msg.lastDay', 2),
                            date_start: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
                            date_end: format(new Date(), 'yyyy-MM-dd')}
                    ],
                    [
                        {label: this.$i18n.tc('msg.lastWeek', 7),
                            date_start: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
                            date_end: format(new Date(), 'yyyy-MM-dd')}
                    ],
                    [
                        {label: this.$i18n.tc('msg.lastMonth', 30),
                            date_start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                            date_end: format(new Date(), 'yyyy-MM-dd')},
                        {label: this.$i18n.tc('msg.thisMonth', 30),
                            date_start: format(new Date((new Date).getFullYear(), (new Date).getMonth(), 1), 'yyyy-MM-dd'),
                            date_end: format(new Date(), 'yyyy-MM-dd')},
                        {label: this.$i18n.tc('msg.previousMonth', 30),
                            date_start: format(new Date(subMonths(new Date(), 1).getFullYear(), subMonths(new Date(), 1).getMonth(), 1), 'yyyy-MM-dd'),
                            date_end: format(subDays(new Date((new Date).getFullYear(), (new Date).getMonth(), 1), 1), 'yyyy-MM-dd')}
                    ]
                ]
            }
        },
        computed: {
            getLabel() {
                let dateStart = '...';
                let dateEnd = '...';

                if (this.localDateStart !== undefined && this.localDateStart !== null) {
                    dateStart = this.localDateStart;
                }

                if (this.localDateEnd !== undefined && this.localDateEnd !== null) {
                    dateEnd = this.localDateEnd;
                }

                if (dateStart === '...' && dateEnd === '...') return null;

                return this.dateString(dateStart) + ' - ' + this.dateString(dateEnd);
            },
            getStyle() {
                let style = {};

                if (this.width != null) {
                    style['max-width'] = this.width + 'px'
                }

                return style;
            }
        },
        watch: {
            dateStart(v) {
                this.localDateStart = v;
                this.localDateStartInit = v;
            },
            dateEnd(v) {
                this.localDateEnd = v;
                this.localDateEndInit = v;
            }
        },
        methods: {
            setPreset(dateStart, dateEnd) {
                this.localDateStartDialog = dateStart;
                this.localDateEndDialog = dateEnd;
            },
            clearDate() {
                this.localDateStartDialog = null;
                this.localDateEndDialog = null;
                this.apply();
            },
            cancel() {
                this.localDateStartDialog = this.localDateStart;
                this.localDateEndDialog = this.localDateEnd;
                this.dialogModel = false;
            },
            apply() {
                this.localDateStart = this.localDateStartDialog;
                this.localDateEnd = this.localDateEndDialog;
                this.$emit('update:date-start', this.localDateStart);
                this.$emit('update:date-end', this.localDateEnd);
                this.$emit('change');
                this.dialogModel = false;
            }
        }
    }
</script>

<style lang="scss">
    .date-range {}
    .date-range__presets {
        margin: 10px 0;
        line-height: 20px;
        display: flex;
    }
    .date-range__presets-col {
        margin-right: 30px;

        &:last-child {
            margin-right: 0;
        }
    }
    .date-range__preset {
        cursor: pointer;
    }
</style>